import React, { useMemo } from 'react';
import './Orders.scss';
import Lottie from 'lottie-react';
import { map, sortBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { useGetOrdersQuery, useUpdateOrderItemStatusMutation } from '../store/order/orderApi';
import loader from '../assets/lotties/loader.json';
import { IGetOrderResponse } from '../proto/order/order';
import { IGetCustomizationGroupSelectionResponse } from '../proto/order/customizationGroupSelection';
import { OrderActionButtons } from './OrderActionButtons/OrderActionButtons';
import { ensureDataUrl } from '../utils/imageHandlers';
import { EOrderItemStatus, eOrderItemStatusToJSON } from '../proto/order/orderItemStatus.enum';

export const Orders = (): JSX.Element => {
	const { data: orders, isLoading: isLoadingOrders } = useGetOrdersQuery({
		statuses: [EOrderItemStatus.PENDING, EOrderItemStatus.PREPARING, EOrderItemStatus.CANCELLED, EOrderItemStatus.COMPLETED],
	});
	const [updateOrderItemStatus] = useUpdateOrderItemStatusMutation();

	const sortedOrders = useMemo(() => {
		return sortBy(orders, (order: IGetOrderResponse) => order.createdAt);
	}, [orders]);

	const getSelectionSummaryString = (selections: IGetCustomizationGroupSelectionResponse[]): string => {
		return map(selections, (selection) => {
			return selection?.name ?? '';
		}).join(', ');
	};

	const mapOrderStatusToString = (status: EOrderItemStatus): string => {
		switch (status) {
			case EOrderItemStatus.PENDING:
				return 'בהמתנה';
			case EOrderItemStatus.PREPARING:
				return 'בהכנה';
			case EOrderItemStatus.COMPLETED:
				return 'הושלם';
			case EOrderItemStatus.CANCELLED:
				return 'בוטל';
			default:
				return eOrderItemStatusToJSON(status);
		}
	};

	const handleItemStatusChange = async (itemId: string, newStatus: EOrderItemStatus): Promise<void> => {
		console.log(`Change status of item ${itemId} to ${eOrderItemStatusToJSON(newStatus)}`);
		await updateOrderItemStatus({ orderItemId: itemId, status: newStatus }).unwrap();
	};

	return (
		<div className="ordersContainer">
			{isLoadingOrders ? (
				<div className="loaderContainer">
					<Lottie animationData={loader} loop style={{ width: 150, height: 150 }} />
				</div>
			) : (
				<div className="ordersList">
					{map(sortedOrders, (order) => (
						<div key={order.id} className="orderCard">
							<div className="orderHeader">שולחן: {order.tableSession.table.name}</div>
							<div className="orderDate">{DateTime.fromISO(order.createdAt).toFormat('dd/MM/yyyy HH:mm')}</div>
							<div className="orderItems">
								{map(order.items, (item) => (
									<div key={item.id} className="orderItem">
										<div className="itemHeader">
											<div className="itemImage">
												<img src={ensureDataUrl(item.image)} alt="title" />
											</div>
											<div className="itemTitleAndDetails">
												<div className="itemTitle">
													<span className="itemName">{item.title}</span>
													<span className="itemQuantity">x{item.quantity}</span>
												</div>
												<div className="itemDetails">
													{item.customizationGroups && item.customizationGroups.length > 0 && (
														<div className="itemCustomizations">
															{map(item.customizationGroups, (group) => (
																<div key={group.id} className="customizationGroup">
																	<div className="groupTitle">{group.title}:</div>
																	<div className="groupSelections">{getSelectionSummaryString(group.selections)}</div>
																</div>
															))}
														</div>
													)}
												</div>
											</div>
											<div className="itemActionButtons">
												<span className="itemStatus">{mapOrderStatusToString(item.status)}</span>
												<span>עדכן ל:</span>
												<OrderActionButtons
													status={item.status}
													onClick={(newStatus): Promise<void> => handleItemStatusChange(item.id, newStatus)}
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					))}
					{orders?.length === 0 && <p>No pending orders.</p>}
				</div>
			)}
		</div>
	);
};
